import React, { useState, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'
import ImageSkeleton from '@/components/shared/ImageSkeleton'

const BlogsCards = ({ blogs = [] }) => {
    const { t } = useTranslation()
    const router = useRouter()
    const lang = router.query.lang
    const [currentPage, setCurrentPage] = useState(1)
    const [loadedImages, setLoadedImages] = useState({})
    const [failedImages, setFailedImages] = useState({})
    const itemsPerPage = 6

    const totalPages = Math.ceil(blogs.length / itemsPerPage)

    const currentBlogs = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage
        const endIndex = startIndex + itemsPerPage
        return blogs.slice(startIndex, endIndex)
    }, [currentPage, blogs])

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const getPageNumbers = () => {
        const pages = []
        const maxVisible = 4

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            if (currentPage <= 2) {
                for (let i = 1; i <= maxVisible; i++) {
                    pages.push(i)
                }
                pages.push('...')
            } else if (currentPage >= totalPages - 1) {
                pages.push('...')
                for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) {
                    pages.push(i)
                }
            } else {
                pages.push('...')
                for (let i = currentPage - 1; i <= currentPage + 2; i++) {
                    pages.push(i)
                }
                pages.push('...')
            }
        }
        return pages
    }

    return (
        <div className="max-w-[90%] mx-auto pt-[40px] pb-[100px] lg:py-[100px]">
            <h1 className="text-[32px] md:text-[48px] lg:text-[80px] font-[500] text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884] mb-[32px] leading-tight md:text-left text-center">
                {t('navlinks.blogs')}
            </h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[24px] mb-[32px]">
                {currentBlogs.map((blog) => (
                    <div
                        key={blog.id}
                        className="bg-[#F5F7F9] rounded-[40px] overflow-hidden"
                    >
                        <div className="flex flex-col h-full px-[16px] py-[24px] gap-[16px]">
                            <div className="relative w-full h-[200px] lg:h-[220px] overflow-hidden">
                                {!loadedImages[blog.id] && (
                                    <ImageSkeleton className="absolute inset-0 w-full h-full rounded-[20px]" />
                                )}
                                {!!blog.media?.[0] && !failedImages[blog.id] && (
                                    <Image
                                        src={blog.media[0]}
                                        alt={blog.title}
                                        fill
                                        priority
                                        onLoad={() => setLoadedImages((prev) => ({ ...prev, [blog.id]: true }))}
                                        onError={() => {
                                            setFailedImages((prev) => ({ ...prev, [blog.id]: true }))
                                            setLoadedImages((prev) => ({ ...prev, [blog.id]: false }))
                                        }}
                                        className={`object-cover object-[center_-10px] rounded-[20px] transition-opacity duration-300 ${loadedImages[blog.id] ? 'opacity-100' : 'opacity-0'}`}
                                    />
                                )}
                            </div>

                            <div className="flex flex-col flex-grow">
                                <p className="text-[#222] text-[16px] md:text-[20px] font-[600] leading-relaxed mb-[20px] flex-grow line-clamp-3">
                                    {blog.title}
                                </p>
                                <Link href={lang ? `/blogs/${blog.id}?lang=${lang}` : `/blogs/${blog.id}`} className="relative self-end overflow-hidden w-[160px] h-[48px] flex items-center justify-center rounded-full text-white hover:text-[#091F59] transition-colors duration-300 group">
                                    <span className="absolute inset-0 bg-gradient-to-b from-[#091F59] to-[#1A3884] border border-[#091F59] transition-opacity duration-300 group-hover:opacity-0"/>
                                    <span className="absolute inset-0 bg-white opacity-0 border rounded-full border-[#091F59] transition-opacity duration-300 group-hover:opacity-100" />
                                    <span className="relative z-10 text-[16px] font-[400]">
                                        {t('blogs.read-more')}
                                    </span>
                                </Link>

                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-[8px]">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="w-[36px] h-[36px] flex items-center justify-center rounded-[8px] border-[1.5px] border-[#E2E8F0] text-[#8B8B8B] hover:text-[#091F59] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="Previous page"
                    >
                        <IoIosArrowBack size={16} />
                    </button>

                    {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                            <span key={`ellipsis-${index}`} className="w-[36px] h-[36px] flex items-center justify-center text-[14px] text-[#8B8B8B] border-[1.5px] border-[#E2E8F0] rounded-[8px]">
                                ...
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`w-[36px] h-[36px] flex items-center justify-center rounded-[8px] text-[14px] font-medium transition-colors ${currentPage === page
                                    ? 'bg-gradient-to-b from-[#091F59] to-[#1A3884] text-white hover:opacity-90'
                                    : 'bg-clip-text text-transparent bg-gradient-to-b from-[#091F59] to-[#1A3884] hover:text-[#091F59] hover:bg-[#F5F7F9] border-[1.5px] border-[#E2E8F0]'
                                    }`}
                            >
                                {page}
                            </button>
                        )
                    ))}

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="w-[36px] h-[36px] flex items-center justify-center rounded-[8px] border-[1.5px] border-[#E2E8F0] text-[#8B8B8B] hover:text-[#091F59] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="Next page"
                    >
                        <IoIosArrowForward size={16} />
                    </button>
                </div>
            )}
        </div>
    )
}

export default BlogsCards
